import type { Swiper as TSwiper } from "swiper/types";
import type { Swiper as SwiperEvent } from "swiper";

import React, { CSSProperties } from "react";
import { Swiper, SwiperRef, SwiperSlide } from "swiper/react";
import { Autoplay, Grid, Keyboard, Navigation, Pagination, Virtual } from "swiper/modules";

import Icon from "src/components/icon";
import { findParentElementWithClassname } from "src/utils/elements";
import { addListener, removeListener } from "src/utils/events";

import "./swiper.slider.scss";

import "swiper/css";
import "swiper/css/grid";
import "swiper/css/pagination";

type State = {
    height: undefined | number;
    width: undefined | number;
}

export default class SwiperSlider extends React.Component<SliderProps, State> {

    static defaultProps: Partial<SliderProps> = {
        initialSlide: 0,
        pagination: true,
        navigation: false,
        loop: false,
        navigation_position: "sides"
    };

    public state: State = {
        height: undefined,
        width: undefined,
    }

    onTabChange(activeTab: number) {
        if (this.props.onTabChange) this.props.onTabChange(activeTab);
        setTimeout(this.resize, 200);
    }

    get style() {
        const outterStyles = this.props.style || {};
        return {
            "--swiper-pagination-color": this.props.pagination_selected_color,
            "--swiper-pagination-bullet-inactive-color": this.props.pagination_unselected_color,
            "--swiper-navigation-color": this.props.navigation_color,
            "--swiper-navigation-bg": this.props.navigation_bg,
            "--swiper-navigation-position": this.props.navigation_position === "sides" ? "-25px" : "25px",
            maxWidth: this.props.isFullWidth ? "100vw" : "calc(100vw - 32px)",
            ...outterStyles,
        } as CSSProperties;
    }

    private swiper: TSwiper | undefined = undefined;
    private divRef: React.RefObject<HTMLDivElement> = React.createRef();
    private swiperRef: React.RefObject<SwiperRef> = React.createRef();

    private slidesRefs: Array<React.RefObject<HTMLDivElement>> = React.Children.toArray(this.props.children).map(() => React.createRef());

    componentDidMount() {
        setTimeout(this.resize, 200);
        addListener('contents-changed', this.resize);
        addListener('resize', this.resize);
        this.forceUpdate();
    }

    componentWillUnmount(): void {
        removeListener('contents-changed', this.resize);
        removeListener('resize', this.resize);
    }

    public slideTo(to: number = this.props.initialSlide || 0) {
        try { this.swiper?.slideTo(to); } catch (err) {}
    }

    resize = () => {
        try {
            const swiperRef = this.swiperRef.current;
            if (!swiperRef) return;
            const ref = this.slidesRefs[swiperRef.swiper.activeIndex];
            const el = ref.current;
            if (!el) return;
            const element = this.divRef.current;
            if (!element) return;
            const MAX_WIDTH = window.innerWidth - (24*2 + 20);
            const height = Math.min(el.offsetHeight, 300);
            const width = Math.min(MAX_WIDTH, element.offsetWidth);
            this.setState({ height, width });
        } catch (err) {}
    }

    onTouchStart = (swiper: SwiperEvent, event: MouseEvent | TouchEvent | PointerEvent) => {
        const mouseWrapper = findParentElementWithClassname(event.target as HTMLElement, 'mouse-trap');
        if (mouseWrapper) { swiper.allowTouchMove = false; }
    }

    onTouchEnd = (swiper: SwiperEvent, event: MouseEvent | TouchEvent | PointerEvent) => {
        swiper.allowTouchMove = true;
    }

    render(): React.ReactNode {
        const { className = "", navigation } = this.props;
        const children = React.Children.toArray(this.props.children);

        return <div ref={this.divRef} className={`relative slider-component ${className}`} style={this.style}>
        <Swiper
            ref={this.swiperRef}
            style={this.style}
            onSwiper={(swiper) => this.swiper = swiper}
            onTouchStart={this.onTouchStart}
            onTouchEnd={this.onTouchEnd}
            initialSlide={this.props.initialSlide}
            centeredSlides={this.props.centeredSlides}
            height={this.props.height ?? (this.props.autoHeight ? undefined : this.state.height)}
            width={this.props.width ?? this.state.width}
            autoHeight={false}
            loop={this.props.loop}
            modules={[Autoplay, Grid, Keyboard, Pagination, Navigation, Virtual]}
            virtual={this.props.virtual}
            navigation={this.props.navigation ? { nextEl: `.custom_next_${this.props.id}`, prevEl: `.custom_prev_${this.props.id}` } : false}
            pagination={this.props.pagination}
            slidesPerView={this.props.slidesPerView}
            spaceBetween={this.props.spaceBetween}
            autoplay={this.props.autoplay}
            breakpoints={this.props.breakpoints}
            onSlideChange={(e) => this.onTabChange(e.activeIndex)}
            keyboard={true}
        >
            {children.map((swiperSlide: any, idx: number) =><SwiperSlide key={`swiperSlide_${idx}`}>
                {React.cloneElement(swiperSlide, { ref: this.slidesRefs[idx] } as any)}
            </SwiperSlide>)}
        </Swiper>
        {navigation ?
            <>
                <button className={`custom_prev_${this.props.id} swiper-button-prev-custom`}>
                    <Icon icon="ChevronLeft"/>
                </button>
                <button className={`custom_next_${this.props.id} swiper-button-next-custom`}>
                    <Icon icon="ChevronRight"/>
                </button>
            </>
            : null
        }
        </div>
        }
    }
